import React, { useEffect, useState } from "react";

interface ArrowProps {
  fromId: string; // ID of the source div
  toId: string; // ID of the target div
  type?: "mobile";
  dashed?: boolean;
  fromMiddle?: boolean;
  fromEnd?: boolean;
}

const Arrow: React.FC<ArrowProps> = ({
  fromId,
  toId,
  type,
  dashed,
  fromMiddle,
  fromEnd,
}) => {
  const [linePath, setLinePath] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  // Function to update the line path based on the positions of the elements
  const updateLine = () => {
    const fromElement = document.getElementById(fromId);
    const toElement = document.getElementById(toId);

    if (fromElement && toElement) {
      const rect1 = fromElement.getBoundingClientRect();
      const rect2 = toElement.getBoundingClientRect();

      const startX =
        type === "mobile"
          ? fromMiddle
            ? rect1.left + rect1.width / 2 + window.scrollX
            : fromEnd
              ? rect1.right - 4 + window.scrollX
              : rect1.left + 20 + window.scrollX
          : rect1.right + window.scrollX; // Account for scrolling
      const startY =
        type === "mobile"
          ? rect1.bottom + window.scrollY
          : rect1.top + rect1.height / 2 + window.scrollY;
      const endX =
        type === "mobile"
          ? fromMiddle
            ? rect2.left + rect2.width / 2 + window.scrollX
            : fromEnd
              ? rect2.right - 4 + window.scrollX
              : rect2.left + 20 + window.scrollX
          : rect2.left + window.scrollX;
      const endY =
        type === "mobile"
          ? rect2.top + window.scrollY
          : rect2.top + rect2.height / 2 + window.scrollY;

      setLinePath(`M${startX},${startY} L${endX},${endY}`);
      setIsVisible(true);
    } else {
      setIsVisible(false);
      setLinePath("");
    }
  };

  // Handler to detect transition end on the accordion
  const handleAccordionTransitionEnd = () => {
    updateLine(); // Update the arrow when the accordion transition ends
  };

  useEffect(() => {
    // Set up listeners for resize, scroll, and accordion transitions
    window.addEventListener("resize", updateLine);
    document.getElementById("outlet")?.addEventListener("scroll", updateLine);
    const accordionElement = document.getElementById(fromId);
    if (accordionElement) {
      accordionElement.addEventListener(
        "transitionend",
        handleAccordionTransitionEnd,
      );
    }

    // Clean up event listeners on unmount or dependency change
    return () => {
      window.removeEventListener("resize", updateLine);
      document
        .getElementById("outlet")
        ?.removeEventListener("scroll", updateLine);
      if (accordionElement) {
        accordionElement.removeEventListener(
          "transitionend",
          handleAccordionTransitionEnd,
        );
      }
    };
  }, [fromId, toId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateLine();
    }, 100); // Delay for 300ms, adjust based on your animation duration

    return () => {
      clearTimeout(timer); // Cleanup the timer on component unmount
    };
  }, [fromId, toId]);

  if (!isVisible) return null;

  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <defs>
        <marker
          id={`arrowhead-${toId}-${fromId}-${type}`}
          markerWidth="10"
          markerHeight="8"
          refX="10"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="black" />
        </marker>
      </defs>
      <path
        d={linePath}
        stroke="black"
        strokeWidth="1.5"
        fill="none"
        markerEnd={dashed ? "" : `url(#arrowhead-${toId}-${fromId}-${type})`}
        strokeDasharray={dashed ? "4 4" : undefined}
      />
    </svg>
  );
};

export default Arrow;
